import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { History, Loader2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deletePayrollEntry, listPayrollEntries } from '../../services/api';
import type { PayrollEntryItem } from '../../services/api/types.gen';
import { PaginationBar } from './PaginationBar';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

const PAGE_SIZE = 25;

/** Payrolls already created — one row per payroll date, drilling into its visits. */
export const PaidPayrollTab: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [entries, setEntries] = useState<PayrollEntryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<PayrollEntryItem | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listPayrollEntries({
        query: { page, per_page: PAGE_SIZE },
        throwOnError: true,
      });
      if (response.data) {
        setEntries(response.data.items);
        setTotal(response.data.total);
        setTotalPages(response.data.total_pages);
      }
    } catch (error) {
      console.error('Failed to load payrolls:', error);
      toast.error('Failed to load payrolls. Please try again.');
    } finally {
      setIsLoading(false);
    }
    // toast is intentionally omitted: useToast returns a fresh object every
    // render, so including it would refetch in a loop (OASISManager pattern).
  }, [page]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleDelete = async () => {
    if (!entryToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deletePayrollEntry({
        path: { payroll_entry_id: entryToDelete.id },
        throwOnError: true,
      });
      toast.success(
        `Payroll deleted. ${response.data.released_count} visits moved back to Unpaid.`
      );
      setEntryToDelete(null);
      // Step back a page when the delete emptied the current one.
      if (entries.length === 1 && page > 1) {
        setPage(p => p - 1);
      } else {
        await fetchEntries();
      }
    } catch (error) {
      console.error('Failed to delete payroll:', error);
      toast.error(
        getBusinessErrorMessage(error) ??
          'Failed to delete payroll. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className='space-y-6'>
      {isLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>
            Loading payrolls...
          </span>
        </div>
      ) : entries.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <History className='h-12 w-12 mx-auto mb-4 text-gray-300' />
          <p>No payrolls created yet</p>
          <p className='text-sm'>Create one from the Unpaid tab</p>
        </div>
      ) : (
        <div className='space-y-4'>
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payroll Date</TableHead>
                  <TableHead>Clinic</TableHead>
                  <TableHead className='text-right'>Visits</TableHead>
                  <TableHead className='w-20 text-center'>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <TableRow
                    key={entry.id}
                    className='cursor-pointer'
                    onClick={() => navigate(`/payroll-manager/${entry.id}`)}
                  >
                    <TableCell className='text-sm font-medium'>
                      {formatDateToMMDDYYYY(entry.payroll_date)}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {entry.clinic_name}
                    </TableCell>
                    <TableCell className='text-right text-sm'>
                      {entry.visit_count}
                    </TableCell>
                    <TableCell className='w-20 text-center'>
                      <Button
                        size='sm'
                        variant='ghost'
                        aria-label='Delete payroll'
                        onClick={e => {
                          e.stopPropagation();
                          setEntryToDelete(entry);
                        }}
                      >
                        <Trash2 className='h-4 w-4 text-destructive' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            itemsLabel='payrolls'
            onPageChange={setPage}
          />
        </div>
      )}

      <ConfirmationDialog
        open={!!entryToDelete}
        title='Delete payroll?'
        message={
          entryToDelete
            ? `This deletes the ${formatDateToMMDDYYYY(entryToDelete.payroll_date)} ${entryToDelete.clinic_name} payroll and moves its ${entryToDelete.visit_count} visits back to Unpaid.`
            : ''
        }
        confirmLabel='Delete'
        loading={isDeleting}
        onAccept={handleDelete}
        onCancel={() => setEntryToDelete(null)}
      />
    </div>
  );
};
